/**
 * Contextual drawing toolbar — appears when a drawing is selected.
 * Matches the design in features/pulsarix-toolbar.html.
 *
 * Shows: stroke color · stroke width · stroke style · visibility ·
 *        lock · delete · deselect.
 *
 * Per-instance overrides are written directly to the drawing object
 * via typed update reducers (updateHorizontalLine, etc.). Type-level
 * defaults live in ChartTheme via the tool settings framework.
 */
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
  useActiveUi,
} from "@/state/hooks";
import {
  removeHorizontalLine,
  removeVerticalLine,
  removeDiagonalLine,
  removePath,
  removeFib,
  removePosition,
  removePriceRangeBox,
  removeTextLabel,
  updateHorizontalLine,
  updateVerticalLine,
  updateDiagonalLine,
  updatePath,
  updateFib,
  updatePosition,
  updatePriceRangeBox,
  updateTextLabel,
  setSelectedDrawing,
  setFibLevelsDialogOpen,
  setSettingsToolId,
  type DrawingKind,
  type LineStyleName,
} from "@/state/slices/uiSlice";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { selectEffectiveTheme } from "@/state/selectors/effectiveTheme";
import { formatColor, parseColor } from "@/utils/colorWithAlpha";
import { toolById } from "@/tools/_shared/registry";
import { toolIdForKind } from "@/components/Chart/drawingMenu";
import { LineStyleSelect } from "@/tools/_shared/LineStyleSelect";
import styles from "./DrawingToolbar.module.css";

// ---- icons (18×18 viewBox, 1.5px stroke) ----

const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/>
      <circle cx="8" cy="8" r="2"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 2l12 12M6.5 6.6A2 2 0 0010 9.5"/>
      <path d="M8 3C4.5 3 2 8 2 8s.8 1.4 2.3 2.7M8 13c3.5 0 6-5 6-5s-.8-1.5-2.4-2.8"/>
    </svg>
  );

const LockIcon = ({ locked }: { locked: boolean }) =>
  locked ? (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="7.5" width="10" height="7" rx="1.5"/>
      <path d="M5.5 7.5V5a2.5 2.5 0 015 0v2.5"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="7.5" width="10" height="7" rx="1.5"/>
      <path d="M5.5 7.5V5a2.5 2.5 0 014.5-1.5"/>
    </svg>
  );

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.5 4h11M6 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4"/>
    <path d="M4 4l.7 8.5a1 1 0 001 .95h4.6a1 1 0 001-.95L12 4"/>
    <path d="M6.5 7v4M9.5 7v4"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
    <path d="M2 2l10 10M12 2L2 12"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Counterclockwise arrow — "reset this drawing's appearance to the global default".
const ResetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2.5 8a5.5 5.5 0 1 1 1.6 3.9" />
    <path d="M2.2 12.2 4.1 11.9 3.8 9.9" />
  </svg>
);


/** Kinds that support per-instance color/width/style. */
const LINE_KINDS: DrawingKind[] = ["hline", "vline", "diagonal", "path"];
/** Kinds that support visibility + lock only (no per-instance stroke). */
const VISIBILITY_ONLY_KINDS: DrawingKind[] = ["fib", "position", "priceRange"];

/** Per-instance appearance-override keys per kind. Each falls back to the
 *  tool-level (theme) default, so clearing them re-inherits the global —
 *  this is what the toolbar's "Reset to global" does. Kinds absent here
 *  (fib / position / priceRange) carry no theme-backed appearance override,
 *  so there is nothing to reset. */
const RESET_STYLE_KEYS: Partial<Record<DrawingKind, string[]>> = {
  hline: ["color", "width", "style", "textColor", "textHPos", "textVPos"],
  vline: ["color", "width", "style"],
  diagonal: ["color", "width", "style"],
  path: ["color", "width", "style"],
  text: ["color", "fontSize", "bold"],
};

/** ChartTheme keys holding each line kind's stroke defaults, so the toolbar
 *  controls show the effective (global) value when a line has no per-instance
 *  override — matching what the renderer draws (`line.x ?? theme.x`). */
const STROKE_THEME_KEYS: Partial<
  Record<DrawingKind, { color: string; width: string; style: string }>
> = {
  hline: { color: "hlineColor", width: "hlineWidth", style: "hlineStyle" },
  vline: { color: "vlineColor", width: "vlineWidth", style: "vlineStyle" },
  diagonal: { color: "trendColor", width: "trendWidth", style: "trendStyle" },
  path: { color: "arrowColor", width: "arrowWidth", style: "arrowStyle" },
};

type StrokeOverride = { color?: string; width?: number; style?: LineStyleName };
type VisibilityOverride = { visible?: boolean; locked?: boolean };


export function DrawingToolbar() {
  const dispatch = useAppDispatch();
  const tabId = useActiveTabId();
  const ui = useActiveUi();
  const sel = ui.selectedDrawing;
  // The toolbar lives in the chart subtree (color-scheme:dark); pin it to the
  // app theme so its native <select> popups follow light/dark.
  const appTheme = useAppSelector((s) => s.appearance.theme);
  // Effective theme supplies the tool-level label defaults a per-line text
  // override falls back to, so the toolbar controls show the resolved value.
  const effectiveTheme = useAppSelector((s) => selectEffectiveTheme(s, tabId));

  if (!sel) return null;

  // Resolve the live drawing object to read current per-instance values.
  const drawing = resolveDrawing(ui, sel.kind, sel.id);
  if (!drawing) return null;

  const visible = (drawing as VisibilityOverride).visible !== false;
  const locked = !!(drawing as VisibilityOverride).locked;
  const isLine = LINE_KINDS.includes(sel.kind);
  const stroke: StrokeOverride = isLine
    ? {
        color: (drawing as StrokeOverride).color,
        width: (drawing as StrokeOverride).width,
        style: (drawing as StrokeOverride).style,
      }
    : {};

  function patchVisible(v: boolean) {
    patchDrawing(dispatch, tabId, sel!, { visible: v });
  }
  function patchLocked(l: boolean) {
    patchDrawing(dispatch, tabId, sel!, { locked: l });
  }
  function patchColor(color: string) {
    patchDrawing(dispatch, tabId, sel!, { color });
  }
  function patchWidth(width: number) {
    patchDrawing(dispatch, tabId, sel!, { width });
  }
  function patchStyle(style: LineStyleName) {
    patchDrawing(dispatch, tabId, sel!, { style });
  }
  function patchText(text: string) {
    patchDrawing(dispatch, tabId, sel!, { text });
  }
  function patchTextColor(textColor: string) {
    patchDrawing(dispatch, tabId, sel!, { textColor });
  }
  function patchTextHPos(textHPos: "left" | "right") {
    patchDrawing(dispatch, tabId, sel!, { textHPos });
  }
  function patchTextVPos(textVPos: "above" | "below") {
    patchDrawing(dispatch, tabId, sel!, { textVPos });
  }
  // Clear every per-instance appearance override for this kind, so the
  // drawing re-inherits the tool-level (global) defaults. Content/geometry
  // (text, points, prices, visibility, lock) is left untouched.
  function resetToGlobal() {
    const keys = RESET_STYLE_KEYS[sel!.kind];
    if (!keys) return;
    patchDrawing(
      dispatch,
      tabId,
      sel!,
      Object.fromEntries(keys.map((k) => [k, undefined])),
    );
  }
  function deleteDrawing() {
    deleteSelectedDrawing(dispatch, tabId, sel!);
    dispatch(setSelectedDrawing({ tabId, selected: null }));
  }
  function deselect() {
    dispatch(setSelectedDrawing({ tabId, selected: null }));
  }
  function openSettings() {
    const tid = toolIdForKind(sel!.kind);
    if (tid === "fib") {
      dispatch(setFibLevelsDialogOpen(true));
    } else if (tid && toolById(tid)?.settings) {
      dispatch(setSettingsToolId(tid));
    }
  }
  const hasSettings = (() => {
    const tid = toolIdForKind(sel.kind);
    return tid === "fib" || (tid !== null && !!toolById(tid)?.settings);
  })();
  // "Reset to global" is offered for kinds with appearance overrides, and
  // only enabled when this drawing actually has at least one set (else it's
  // a no-op).
  const resetKeys = RESET_STYLE_KEYS[sel.kind];
  const hasOverride =
    !!resetKeys &&
    resetKeys.some((k) => (drawing as Record<string, unknown>)[k] !== undefined);

  // Stroke controls fall back to the effective theme (the global default the
  // renderer uses) so they stay correct after a "Reset to global".
  const strokeThemeKeys = STROKE_THEME_KEYS[sel.kind];
  const themeStroke = effectiveTheme as unknown as Record<string, unknown>;
  const { color: colorHex, opacity } = parseColor(
    stroke.color ??
      (strokeThemeKeys && (themeStroke[strokeThemeKeys.color] as string)) ??
      "#2962FF",
  );
  const width =
    stroke.width ??
    (strokeThemeKeys && (themeStroke[strokeThemeKeys.width] as number)) ??
    1;
  const lineStyle =
    stroke.style ??
    (strokeThemeKeys && (themeStroke[strokeThemeKeys.style] as LineStyleName)) ??
    "solid";
  // Inline text content for the text tool and for a horizontal line's
  // optional label.
  const hasTextInput = sel.kind === "text" || sel.kind === "hline";
  const textValue = hasTextInput
    ? ((drawing as { text?: string }).text ?? "")
    : "";
  // Per-line horizontal-line label controls. Colour defaults to the line's
  // stroke; placement falls back to the tool-level default (theme).
  const hlineText = drawing as {
    textColor?: string;
    textHPos?: "left" | "right";
    textVPos?: "above" | "below";
  };
  const { color: textColorHex, opacity: textOpacity } = parseColor(
    hlineText.textColor ?? effectiveTheme.hlineTextColor ?? stroke.color ?? "#2962FF",
  );
  const textHPos =
    hlineText.textHPos ?? effectiveTheme.hlineTextHPos ?? "left";
  const textVPos =
    hlineText.textVPos ?? effectiveTheme.hlineTextVPos ?? "above";

  return (
    <div
      className={styles.toolbar}
      role="toolbar"
      aria-label="Drawing options"
      style={{ colorScheme: appTheme }}
    >
      {/* Visibility */}
      <button
        type="button"
        className={`${styles.btn} ${!visible ? styles.btnActive : ""}`}
        title={visible ? "Hide drawing" : "Show drawing"}
        aria-pressed={!visible}
        onClick={() => patchVisible(!visible)}
      >
        <EyeIcon open={visible} />
      </button>

      {/* Lock */}
      <button
        type="button"
        className={`${styles.btn} ${locked ? styles.btnActive : ""}`}
        title={locked ? "Unlock drawing" : "Lock drawing"}
        aria-pressed={locked}
        onClick={() => patchLocked(!locked)}
      >
        <LockIcon locked={locked} />
      </button>

      {/* Quick-edit text content for text labels and horizontal-line labels. */}
      {hasTextInput && (
        <>
          <div className={styles.divider} />
          <input
            type="text"
            className={styles.textInput}
            value={textValue}
            placeholder={sel.kind === "hline" ? "Label…" : "Text…"}
            onChange={(e) => patchText(e.target.value)}
            aria-label="Edit text"
          />
        </>
      )}

      {/* Per-line label appearance for a horizontal line — colour + placement.
          Only shown once the line has label text. */}
      {sel.kind === "hline" && textValue && (
        <>
          <div className={styles.colorWrapper} title="Text colour">
            <ColorPicker
              value={textColorHex}
              opacity={textOpacity}
              onChange={(c, o) => patchTextColor(formatColor(c, o))}
              ariaLabel="Text colour"
            />
          </div>
          <select
            className={`${styles.strokeSelect} ${styles.posSelect}`}
            value={textHPos}
            onChange={(e) => patchTextHPos(e.target.value as "left" | "right")}
            aria-label="Label side"
            title="Label side"
          >
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
          <select
            className={`${styles.strokeSelect} ${styles.posSelect}`}
            value={textVPos}
            onChange={(e) => patchTextVPos(e.target.value as "above" | "below")}
            aria-label="Label position"
            title="Label position"
          >
            <option value="above">Above</option>
            <option value="below">Below</option>
          </select>
        </>
      )}

      {isLine && (
        <>
          <div className={styles.divider} />

          {/* Stroke color */}
          <div className={styles.colorWrapper} title="Stroke colour">
            <ColorPicker
              value={colorHex}
              opacity={opacity}
              onChange={(c, o) => patchColor(formatColor(c, o))}
              ariaLabel="Stroke colour"
            />
          </div>

          {/* Stroke width */}
          <div className={styles.strokeWidget} title="Stroke width">
            <div
              className={styles.strokeLine}
              style={{ height: Math.max(1, width) + "px" }}
            />
            <select
              className={styles.strokeSelect}
              value={width}
              onChange={(e) => patchWidth(Number(e.target.value))}
              aria-label="Stroke width"
            >
              {[1, 2, 3, 4].map((w) => (
                <option key={w} value={w}>{w}px</option>
              ))}
            </select>
          </div>

          {/* Line style — shared visual picker (same as the settings panel). */}
          <LineStyleSelect
            value={lineStyle}
            onChange={patchStyle}
            triggerClassName={styles.styleTrigger}
          />
        </>
      )}

      <div className={styles.divider} />

      {/* Reset to global — clears this drawing's per-instance appearance
          overrides so it follows the tool's global defaults again. */}
      {resetKeys && (
        <button
          type="button"
          className={styles.btn}
          title="Reset appearance to global default"
          onClick={resetToGlobal}
          disabled={!hasOverride}
        >
          <ResetIcon />
        </button>
      )}

      {/* Settings */}
      {hasSettings && (
        <button
          type="button"
          className={`${styles.btn} ${styles.btnSettings}`}
          title="Drawing settings"
          onClick={openSettings}
        >
          <SettingsIcon />
        </button>
      )}

      {/* Delete */}
      <button
        type="button"
        className={`${styles.btn} ${styles.btnDanger}`}
        title="Delete drawing"
        onClick={deleteDrawing}
      >
        <TrashIcon />
      </button>

      <div className={styles.divider} />

      {/* Close / deselect */}
      <button
        type="button"
        className={styles.btnClose}
        title="Deselect"
        onClick={deselect}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

// ---- helpers ----

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveDrawing(ui: ReturnType<typeof useActiveUi>, kind: DrawingKind, id: string): any {
  switch (kind) {
    case "hline": return ui.horizontalLines.find((l) => l.id === id);
    case "vline": return ui.verticalLines.find((l) => l.id === id);
    case "diagonal": return ui.diagonalLines.find((l) => l.id === id);
    case "path": return ui.paths.find((p) => p.id === id);
    case "fib": return ui.fibRetracements.find((f) => f.id === id);
    case "position": return ui.positions.find((p) => p.id === id);
    case "priceRange": return ui.priceRangeBoxes.find((b) => b.id === id);
    case "text": return ui.textLabels.find((l) => l.id === id);
    default: return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function patchDrawing(dispatch: ReturnType<typeof useAppDispatch>, tabId: string, sel: NonNullable<ReturnType<typeof useActiveUi>["selectedDrawing"]>, patch: Record<string, any>) {
  const { kind, id } = sel;
  switch (kind) {
    case "hline":     dispatch(updateHorizontalLine({ tabId, id, ...patch })); break;
    case "vline":     dispatch(updateVerticalLine({ tabId, id, ...patch })); break;
    case "diagonal":  dispatch(updateDiagonalLine({ tabId, id, ...patch })); break;
    case "path":      dispatch(updatePath({ tabId, id, ...patch })); break;
    case "fib":       dispatch(updateFib({ tabId, id, ...patch })); break;
    case "position":  dispatch(updatePosition({ tabId, id, ...patch })); break;
    case "priceRange": dispatch(updatePriceRangeBox({ tabId, id, ...patch })); break;
    case "text":      dispatch(updateTextLabel({ tabId, id, ...patch })); break;
  }
}

function deleteSelectedDrawing(dispatch: ReturnType<typeof useAppDispatch>, tabId: string, sel: NonNullable<ReturnType<typeof useActiveUi>["selectedDrawing"]>) {
  const { kind, id } = sel;
  switch (kind) {
    case "hline": dispatch(removeHorizontalLine({ tabId, id })); break;
    case "vline": dispatch(removeVerticalLine({ tabId, id })); break;
    case "diagonal": dispatch(removeDiagonalLine({ tabId, id })); break;
    case "path": dispatch(removePath({ tabId, id })); break;
    case "fib": dispatch(removeFib({ tabId, id })); break;
    case "position": dispatch(removePosition({ tabId, id })); break;
    case "priceRange": dispatch(removePriceRangeBox({ tabId, id })); break;
    case "text": dispatch(removeTextLabel({ tabId, id })); break;
  }
}

// Suppress unused warning — VISIBILITY_ONLY_KINDS is referenced in the
// type narrowing comment only; keep it for future per-type patchDrawing.
void VISIBILITY_ONLY_KINDS;
